import type { LibraryBloomLevel } from "@/lib/mock/library"

export type SuggestedDifficulty = "easy" | "medium" | "hard"

const BLOOM_RANK: Record<string, number> = {
  Remember: 0,
  Understand: 1,
  Apply: 2,
  Analyze: 3,
  Evaluate: 4,
  Create: 5,
}

const DIFFICULTY_RANK: Record<SuggestedDifficulty, number> = {
  easy: 0,
  medium: 1,
  hard: 2,
}

export const DIFFICULTY_TAG_LABEL: Record<SuggestedDifficulty, string> = {
  easy: "Low",
  medium: "Normal",
  hard: "High",
}

export function stemHasTagSignal(stem: string): boolean {
  return stem.trim().split(/\s+/).filter(Boolean).length >= 6
}

export function normalizeDifficultyTag(value: string): SuggestedDifficulty | "" {
  const next = value.trim().toLowerCase()
  if (next === "easy" || next === "low") return "easy"
  if (next === "medium" || next === "normal") return "medium"
  if (next === "hard" || next === "high") return "hard"
  return ""
}

const BLOOM_ALIASES: Record<string, LibraryBloomLevel> = {
  Remember: "Remember",
  Remembering: "Remember",
  Recall: "Remember",
  Understand: "Understand",
  Understanding: "Understand",
  Apply: "Apply",
  Applying: "Apply",
  Analyze: "Analyze",
  Analyzing: "Analyze",
  Evaluate: "Evaluate",
  Evaluating: "Evaluate",
  Create: "Create",
  Creating: "Create",
}

export function normalizeBloomTag(value: string): LibraryBloomLevel | "" {
  const next = value.trim()
  if (!next) return ""
  return BLOOM_ALIASES[next] ?? ""
}

export function suggestBloomLevel(stem: string): LibraryBloomLevel | null {
  if (!stemHasTagSignal(stem)) return null
  const text = stem.toLowerCase()
  if (/\b(design|construct|compose|invent|propose|create)\b/.test(text)) {
    return "Create"
  }
  if (/\b(critique|justify|evaluate|assess|argue|judge)\b/.test(text)) {
    return "Evaluate"
  }
  if (
    /\b(except|least likely|differentiate|distinguish|compare|contrast|analyze)\b/.test(
      text,
    )
  ) {
    return "Analyze"
  }
  if (/\b(calculate|compute|solve|demonstrate|apply)\b/.test(text)) {
    return "Apply"
  }
  if (/\b(explain|describe|summarise|summarize|classify|interpret)\b/.test(text)) {
    return "Understand"
  }
  if (
    /\b(define|identify|name|list|recall|what is|principal|is an)\b/.test(text)
  ) {
    return "Remember"
  }
  return null
}

export function suggestDifficultyTag(
  stem: string,
  type: string,
): SuggestedDifficulty | null {
  if (!stemHasTagSignal(stem)) return null
  const text = stem.toLowerCase()
  if (
    /\b(except|least likely|most likely|calculate|compute|multi-step)\b/.test(
      text,
    )
  ) {
    return "hard"
  }
  if (
    type === "true_false" ||
    /\b(define|what is|principal|is an|name the)\b/.test(text)
  ) {
    return "easy"
  }
  return null
}

export function bloomTagsOppose(left: string, right: string): boolean {
  const a = BLOOM_RANK[left]
  const b = BLOOM_RANK[right]
  if (a == null || b == null) return false
  return Math.abs(a - b) >= 3
}

export function difficultyTagsOppose(left: string, right: string): boolean {
  const a = normalizeDifficultyTag(left)
  const b = normalizeDifficultyTag(right)
  if (!a || !b) return false
  return Math.abs(DIFFICULTY_RANK[a] - DIFFICULTY_RANK[b]) >= 2
}
